import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const TimelineBudgetForm = ({ formData, onFormDataChange, errors }) => {
  const handleInputChange = (field, value) => {
    onFormDataChange({
      ...formData,
      [field]: value
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Timeline & Budget</h2>
        <p className="text-muted-foreground">
          Help us understand your timeline and budget expectations
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Timeline Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Project Timeline</h3>
          </div>

          <div className="space-y-4">
            <Input
              label="Preferred Start Date"
              type="date"
              value={formData?.preferredStartDate}
              onChange={(e) => handleInputChange('preferredStartDate', e?.target?.value)}
              error={errors?.preferredStartDate}
              required
            />

            <Input
              label="Required Completion Date"
              type="date"
              value={formData?.requiredCompletionDate}
              onChange={(e) => handleInputChange('requiredCompletionDate', e?.target?.value)}
              error={errors?.requiredCompletionDate}
              required
            />

            <Select
              label="Timeline Flexibility"
              placeholder="How flexible is your timeline?"
              required
              options={[
                { value: 'fixed', label: 'Fixed - Dates cannot change' },
                { value: 'somewhat-flexible', label: 'Somewhat Flexible - 1-2 weeks' },
                { value: 'flexible', label: 'Flexible - 1 month variance' },
                { value: 'very-flexible', label: 'Very Flexible - Open to suggestions' }
              ]}
              value={formData?.timelineFlexibility}
              onChange={(value) => handleInputChange('timelineFlexibility', value)}
              error={errors?.timelineFlexibility}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Timeline Constraints
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                placeholder="Any specific timeline constraints or milestones we should know about?"
                value={formData?.timelineConstraints}
                onChange={(e) => handleInputChange('timelineConstraints', e?.target?.value)}
              />
            </div>
          </div>
        </div>

        {/* Budget Section */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-accent/10 rounded-lg">
              <Icon name="DollarSign" size={20} className="text-accent" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Budget Information</h3>
          </div>

          <div className="space-y-4">
            <Select
              label="Budget Range (UGX)"
              placeholder="Select your budget range"
              required
              options={[
                { value: 'under-5m', label: 'Under 5M UGX' },
                { value: '5m-15m', label: '5M - 15M UGX' },
                { value: '15m-30m', label: '15M - 30M UGX' },
                { value: '30m-50m', label: '30M - 50M UGX' },
                { value: '50m-100m', label: '50M - 100M UGX' },
                { value: 'over-100m', label: 'Over 100M UGX' },
                { value: 'discuss', label: 'Prefer to discuss' }
              ]}
              value={formData?.budgetRange}
              onChange={(value) => handleInputChange('budgetRange', value)}
              error={errors?.budgetRange}
            />

            <Select
              label="Budget Type"
              placeholder="Select budget type"
              required
              options={[
                { value: 'fixed', label: 'Fixed Budget - Cannot exceed' },
                { value: 'estimated', label: 'Estimated Budget - Some flexibility' },
                { value: 'open', label: 'Open Budget - Value-based pricing' }
              ]}
              value={formData?.budgetType}
              onChange={(value) => handleInputChange('budgetType', value)}
              error={errors?.budgetType}
            />

            <Select
              label="Payment Preference"
              placeholder="Select payment preference"
              options={[
                { value: 'milestone', label: 'Milestone-based payments' },
                { value: 'upfront-partial', label: '50% upfront, 50% on completion' },
                { value: 'monthly', label: 'Monthly payments' },
                { value: 'completion', label: 'Full payment on completion' },
                { value: 'discuss', label: 'Open to discussion' }
              ]}
              value={formData?.paymentPreference}
              onChange={(value) => handleInputChange('paymentPreference', value)}
              error={errors?.paymentPreference}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Budget Justification
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                placeholder="Help us understand how you arrived at this budget range"
                value={formData?.budgetJustification}
                onChange={(e) => handleInputChange('budgetJustification', e?.target?.value)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Additional Preferences */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-secondary/10 rounded-lg">
            <Icon name="Settings" size={20} className="text-secondary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Additional Preferences</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <label className="block text-sm font-medium text-foreground">
              Project Delivery Preferences
            </label>
            <div className="space-y-3">
              <Checkbox
                label="Phased Delivery Acceptable"
                checked={formData?.phasedDelivery}
                onChange={(e) => handleInputChange('phasedDelivery', e?.target?.checked)}
              />
              <Checkbox
                label="Remote Work Acceptable"
                checked={formData?.remoteWork}
                onChange={(e) => handleInputChange('remoteWork', e?.target?.checked)}
              />
              <Checkbox
                label="Weekend Work if Needed"
                checked={formData?.weekendWork}
                onChange={(e) => handleInputChange('weekendWork', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-foreground">
              Communication Preferences
            </label>
            <div className="space-y-3">
              <Checkbox
                label="Weekly Progress Reports"
                checked={formData?.weeklyReports}
                onChange={(e) => handleInputChange('weeklyReports', e?.target?.checked)}
              />
              <Checkbox
                label="Regular Video Calls"
                checked={formData?.videoCalls}
                onChange={(e) => handleInputChange('videoCalls', e?.target?.checked)}
              />
              <Checkbox
                label="Dedicated Project Manager"
                checked={formData?.dedicatedPM}
                onChange={(e) => handleInputChange('dedicatedPM', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineBudgetForm;